import React, { useRef, useState, useCallback, useEffect } from "react";
import { Textarea } from "@/components/ui/textarea";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { UseFormReturn } from "react-hook-form";
import { useDispatch } from "react-redux";
import { AppDispatch } from "@/app/store";
import { fetchEmailTemplateVariablesAction } from "@/modules/emailManagement/features/EmailTemplateManagement/slices/emailTemplateVariableListSlice";
import ToggleButtonGroup from "@/modules/emailManagement/features/EmailTemplateManagement/components/ToggleButtonGroup/ToggleButtonGroup";
import EmailTemplateVariableSelector from "@/modules/emailManagement/features/EmailTemplateManagement/components/EmailTemplateVariableSelector/EmailTemplateVariableSelector";
import { EventNotificationFormData } from "../../hooks/useEditEventNotification";
import { Input } from "@/components/ui/input";

interface EventNotificationEmailEditorProps {
  form: UseFormReturn<EventNotificationFormData>;
}

const EventNotificationEmailEditor: React.FC<
  EventNotificationEmailEditorProps
> = ({ form }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [activeTab, setActiveTab] = useState<"edit" | "preview">("edit");
  const { control, getValues, setValue } = form;
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    dispatch(fetchEmailTemplateVariablesAction());
  }, [dispatch]);

  const adjustIframeHeight = useCallback(() => {
    if (iframeRef.current) {
      const doc = iframeRef.current.contentWindow?.document;
      if (doc) iframeRef.current.style.height = `${doc.body.scrollHeight}px`;
    }
  }, []);

  const handleInsertVariableToContent = (variableName: string) => {
    const textarea = document.querySelector(
      "textarea[name='emailContent']",
    ) as HTMLTextAreaElement;
    if (!textarea) return;

    const currentContent = getValues("emailContent");
    const { selectionStart, selectionEnd } = textarea;
    const newContent =
      currentContent.slice(0, selectionStart) +
      variableName +
      currentContent.slice(selectionEnd);

    setValue("emailContent", newContent);

    requestAnimationFrame(() => {
      textarea.selectionStart = textarea.selectionEnd =
        selectionStart + variableName.length;
      textarea.focus();
    });
  };

  const emailContentValue = getValues("emailContent").trim();

  const renderEmailContentSection = () => {
    if (activeTab === "edit") {
      return (
        <FormField
          control={control}
          name="emailContent"
          render={({ field }) => (
            <FormItem className="mt-1">
              <FormControl>
                <Textarea
                  {...field}
                  className="w-full min-h-[300px] h-auto"
                  placeholder="<h1>Welcome!</h1><p>Enter your HTML content here</p>"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      );
    }

    if (emailContentValue) {
      return (
        <iframe
          className="w-full border rounded-md mt-4"
          onLoad={adjustIframeHeight}
          ref={iframeRef}
          sandbox="allow-same-origin"
          srcDoc={`<style>body, html { margin: 0; padding: 0; overflow: hidden; }</style>${emailContentValue}`}
          style={{ minHeight: "300px", width: "100%", border: "none" }}
        />
      );
    }

    return (
      <p className="text-gray-500 min-h-[300px] flex items-center justify-center mt-4">
        No content to preview
      </p>
    );
  };

  return (
    <div className="space-y-4">
      <FormField
        control={control}
        name="emailSubject"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Email Subject</FormLabel>
            <FormControl>
              <Input
                {...field}
                placeholder="e.g., Reminder: *|SessionName|* in *|DaysUntil|* days"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <div>
        <FormLabel>Email Content</FormLabel>
        <div className="flex justify-between items-center mt-2">
          <ToggleButtonGroup
            activeTab={activeTab}
            setActiveTab={setActiveTab}
          />
          {activeTab === "edit" && (
            <EmailTemplateVariableSelector
              onVariableSelect={handleInsertVariableToContent}
            />
          )}
        </div>
        {renderEmailContentSection()}
        {activeTab === "edit" && (
          <p className="text-sm text-gray-500 mt-2">
            {`Enter your email template HTML. Use the "Insert Variable" button to add dynamic content that will be replaced when emails are sent.`}
          </p>
        )}
      </div>
    </div>
  );
};

export default EventNotificationEmailEditor;
